import React from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const SECTIONS = [
  { id: "introduction", title: "Introduction" },
  { id: "information-we-collect", title: "Information We Collect" },
  { id: "on-device", title: "Information Stored on Your Device" },
  { id: "servers", title: "Information Sent to Our Servers" },
  { id: "ai", title: "AI Processing" },
  { id: "analytics-ads", title: "Analytics and Advertising" },
  { id: "cookies", title: "Cookies and Similar Technologies" },
  { id: "how-we-use", title: "How We Use Information" },
  { id: "sharing", title: "Sharing of Information" },
  { id: "retention", title: "Data Retention" },
  { id: "deletion", title: "Data Deletion" },
  { id: "children", title: "Children's Privacy" },
  { id: "security", title: "Security" },
  { id: "transfers", title: "International Data Transfers" },
  { id: "rights", title: "Your Rights (EEA / UK GDPR)" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact" },
];

const UL = ({ children }) => <ul className="space-y-2 pl-1">{children}</ul>;
const LI = ({ children }) => (
  <li className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-decide-forest/70" /><span>{children}</span></li>
);

export default function Privacy() {
  return (
    <LegalLayout
      metaTitle="DECIDE — Privacy Policy"
      metaDescription="Privacy Policy for DECIDE explaining how user information is collected, used, stored, and protected."
      title="Privacy Policy"
      subtitle="How DECIDE collects, uses, and protects your information."
      updated="13 June 2026"
      toc={SECTIONS}
    >
      <LegalSection id="introduction" index={1} title="Introduction">
        <p>
          DECIDE is a decision-making application that helps you think through difficult decisions by structuring your
          options, the factors that matter, your preferences and reasoning, and producing a clear analysis and
          recommendation. DECIDE is a web-based application accessible through mobile and desktop browsers.
        </p>
        <p>
          This Privacy Policy explains how personal information and other data are collected, used, stored, protected,
          and — where applicable — shared when you use DECIDE. It applies to the DECIDE application and the DECIDE
          website.
        </p>
        <p>
          DECIDE is operated by <strong className="text-decide-ink font-semibold">DGG Labs</strong> (“we”, “us”, “our”).
          If you have any questions, contact us at{" "}
          <a className="text-decide-forest underline underline-offset-2" href="mailto:dgg.operations@gmail.com">dgg.operations@gmail.com</a>.
        </p>
      </LegalSection>

      <LegalSection id="information-we-collect" index={2} title="Information We Collect">
        <p>We collect only the information needed to provide DECIDE. Depending on how you use the app, this may include:</p>
        <UL>
          <LI><strong className="text-decide-ink font-semibold">Account information.</strong> If you choose to sign in, we use Google Sign-In (provided through our authentication provider). We receive your email address, your name, your profile picture, and a Google account identifier. You can use much of DECIDE without an account; an account is required to save decisions and to subscribe.</LI>
          <LI><strong className="text-decide-ink font-semibold">Content you create.</strong> The decisions you enter — including the situation you describe, the options and factors, your answers to clarifying questions, the resulting analysis and recommendation, any decision reviews, and your saved “next step”.</LI>
          <LI><strong className="text-decide-ink font-semibold">Preferences and usage.</strong> Your language and appearance (theme) settings, your subscription status, and a counter of how many free analyses you have used.</LI>
          <LI><strong className="text-decide-ink font-semibold">Payment information.</strong> If you subscribe, payments are processed by Stripe. We store a transaction record (plan, amount, currency, status, and Stripe identifiers). We do not receive or store your full card number.</LI>
          <LI><strong className="text-decide-ink font-semibold">Reports you submit.</strong> If you report an AI-generated response, we store the reason you select, any optional details you add, and context about the reported response.</LI>
          <LI><strong className="text-decide-ink font-semibold">Diagnostic and technical information.</strong> Like most online services, our servers and hosting infrastructure automatically generate logs that may include your IP address, browser/device type, and request metadata, used to operate and secure the service.</LI>
        </UL>
        <p>
          We do <strong className="text-decide-ink font-semibold">not</strong> collect your precise location, contacts,
          photos or camera, microphone audio, phone number, or health data. DECIDE does not request these device
          permissions.
        </p>
      </LegalSection>

      <LegalSection id="on-device" index={3} title="Information Stored on Your Device">
        <p>DECIDE stores a small amount of information locally in your browser:</p>
        <UL>
          <LI>Your <strong className="text-decide-ink font-semibold">language</strong> and <strong className="text-decide-ink font-semibold">theme</strong> preferences (in local storage), so the app remembers how you like it.</LI>
          <LI>A temporary copy of a decision you are working on and a post-login redirect (in session storage), used only to continue your flow if you sign in mid-way.</LI>
          <LI>A <strong className="text-decide-ink font-semibold">session cookie</strong> that keeps you signed in (see “Cookies” below).</LI>
        </UL>
        <p>
          Preference data stays on your device and is not used to identify you. This information is removed when you
          clear your browser’s site data or uninstall/stop using the app in that browser.
        </p>
      </LegalSection>

      <LegalSection id="servers" index={4} title="Information Sent to Our Servers">
        <p>
          When you sign in, save a decision, subscribe, or submit a report, the relevant information described above is
          transmitted to our backend so we can provide the service. Our backend is built with FastAPI and stores data
          in a MongoDB database hosted on our infrastructure.
        </p>
        <UL>
          <LI><strong className="text-decide-ink font-semibold">What is transmitted:</strong> your account details, the decisions and answers you create, subscription/payment metadata, and any reports you submit.</LI>
          <LI><strong className="text-decide-ink font-semibold">Why:</strong> to authenticate you, generate and save your analyses, manage your subscription, and keep the service secure.</LI>
          <LI><strong className="text-decide-ink font-semibold">Protection:</strong> data is transmitted over encrypted connections (HTTPS/TLS) and stored with access controls.</LI>
          <LI><strong className="text-decide-ink font-semibold">Retention:</strong> see “Data Retention”.</LI>
        </UL>
      </LegalSection>

      <LegalSection id="ai" index={5} title="AI Processing">
        <p>
          DECIDE uses artificial intelligence to understand your decision and generate the analysis and recommendation.
          To do this, the text you enter and your answers to the clarifying questions are sent to a large language model
          (Anthropic’s Claude) through our integration/proxy provider, which returns the structured analysis.
        </p>
        <UL>
          <LI><strong className="text-decide-ink font-semibold">What may be sent:</strong> the decision description and clarifying answers you provide. Please avoid entering sensitive personal information you would not want processed by an AI service.</LI>
          <LI><strong className="text-decide-ink font-semibold">Why:</strong> solely to produce your decision analysis, questions, trade-offs, and recommendation.</LI>
          <LI><strong className="text-decide-ink font-semibold">Handling by the provider:</strong> the content is processed transiently to return a result and is subject to the AI provider’s terms. According to Anthropic, inputs submitted through its API are not used to train its models. We do not use your content to train our own models.</LI>
        </UL>
      </LegalSection>

      <LegalSection id="analytics-ads" index={6} title="Analytics and Advertising">
        <p>
          DECIDE does <strong className="text-decide-ink font-semibold">not</strong> currently use third-party analytics
          SDKs (such as Google Analytics, Firebase Analytics, PostHog, Mixpanel, or Amplitude), and DECIDE does{" "}
          <strong className="text-decide-ink font-semibold">not</strong> display advertising or use advertising networks.
          We do not track you across other apps or websites.
        </p>
      </LegalSection>

      <LegalSection id="cookies" index={7} title="Cookies and Similar Technologies">
        <p>
          The DECIDE application uses a single strictly-necessary <strong className="text-decide-ink font-semibold">session cookie</strong> to
          keep you signed in after authentication, together with browser local storage for your preferences (as described
          above). We do not use advertising or cross-site tracking cookies.
        </p>
        <p>
          The DECIDE website loads a web font from Google Fonts and, on the landing page, one image from Unsplash. When
          these assets load, those providers may receive your IP address and browser information as a normal part of
          delivering the content. These requests are limited to serving those assets.
        </p>
      </LegalSection>

      <LegalSection id="how-we-use" index={8} title="How We Use Information">
        <p>We use the information we collect to:</p>
        <UL>
          <LI>Provide and operate DECIDE and save the decisions and content you create.</LI>
          <LI>Generate your AI-powered analysis and recommendations.</LI>
          <LI>Manage your account, subscription, and free-usage limits.</LI>
          <LI>Maintain security, prevent abuse, and diagnose technical problems.</LI>
          <LI>Respond to your reports and provide customer support.</LI>
          <LI>Comply with applicable legal obligations.</LI>
        </UL>
      </LegalSection>

      <LegalSection id="sharing" index={9} title="Sharing of Information">
        <p>We do not sell your personal information. We share information only with the service providers needed to run DECIDE, and with authorities where legally required:</p>
        <UL>
          <LI><strong className="text-decide-ink font-semibold">Hosting &amp; platform provider</strong> — operates our servers, database, and authentication proxy.</LI>
          <LI><strong className="text-decide-ink font-semibold">Authentication provider (Google Sign-In)</strong> — verifies your identity when you choose to sign in.</LI>
          <LI><strong className="text-decide-ink font-semibold">AI provider (Anthropic)</strong> — processes your decision text to generate the analysis, via our integration provider.</LI>
          <LI><strong className="text-decide-ink font-semibold">Payment processor (Stripe)</strong> — processes subscription payments.</LI>
          <LI><strong className="text-decide-ink font-semibold">Content delivery (Google Fonts, Unsplash)</strong> — serves the website font and one landing image.</LI>
          <LI><strong className="text-decide-ink font-semibold">Legal authorities</strong> — when required by law or to protect rights, safety, and the integrity of the service.</LI>
        </UL>
      </LegalSection>

      <LegalSection id="retention" index={10} title="Data Retention">
        <p>
          We keep your account information and the decisions you create for as long as your account is active. Payment
          records may be retained as needed to meet legal, tax, and accounting obligations. Reports you submit may be
          retained for safety and abuse-prevention purposes. We retain your account and the decisions you create until
          you choose to delete your account. When you delete your account, your data is removed as described below.
        </p>
      </LegalSection>

      <LegalSection id="deletion" index={11} title="Data Deletion">
        <p>You can permanently delete your account and associated data at any time:</p>
        <UL>
          <LI><strong className="text-decide-ink font-semibold">In the app:</strong> go to <em>Settings → Delete account</em> and confirm. This immediately and permanently deletes your account, your decisions and history, your subscription records, and your active sessions.</LI>
          <LI><strong className="text-decide-ink font-semibold">On the web:</strong> visit <a className="text-decide-forest underline underline-offset-2" href="/delete-account">/delete-account</a> for step-by-step instructions and details on exactly what is deleted and retained.</LI>
        </UL>
        <p>
          Deletion cannot be undone. Certain records (such as payment records required for legal compliance, and reports
          retained for safety) may be kept where the law permits or requires. If you need help, contact{" "}
          <a className="text-decide-forest underline underline-offset-2" href="mailto:dgg.operations@gmail.com">dgg.operations@gmail.com</a>.
        </p>
      </LegalSection>

      <LegalSection id="children" index={12} title="Children's Privacy">
        <p>
          DECIDE is intended for general audiences and is not directed to children under 16. We do not knowingly collect
          personal information from children under 16. If you believe a child has provided us with personal information,
          contact us and we will delete it.
        </p>
      </LegalSection>

      <LegalSection id="security" index={13} title="Security">
        <p>
          We use reasonable technical and organizational measures to protect your information, including encrypted
          connections (HTTPS/TLS), access controls, and HTTP-only session cookies. No method of transmission or storage
          is completely secure, and we cannot guarantee absolute security, but we work to protect your information and to
          respond appropriately to any incident.
        </p>
      </LegalSection>

      <LegalSection id="transfers" index={14} title="International Data Transfers">
        <p>
          Some of the service providers described in this policy may process information in countries other than your
          own, including outside the European Economic Area (for example, in the United States). Where such transfers
          occur, we rely on the providers’ applicable safeguards and contractual protections to keep your information
          protected, as required by applicable law.
        </p>
      </LegalSection>

      <LegalSection id="rights" index={15} title="Your Rights (EEA / UK GDPR)">
        <p>
          If you are in the European Economic Area or the United Kingdom, you have the following rights regarding your
          personal data, subject to applicable law:
        </p>
        <UL>
          <LI>Right of access to your personal data.</LI>
          <LI>Right to rectification of inaccurate data.</LI>
          <LI>Right to erasure (deletion).</LI>
          <LI>Right to restriction of processing.</LI>
          <LI>Right to object to processing.</LI>
          <LI>Right to data portability.</LI>
          <LI>Right to withdraw consent, where processing is based on consent.</LI>
        </UL>
        <p>
          To exercise these rights, contact{" "}
          <a className="text-decide-forest underline underline-offset-2" href="mailto:dgg.operations@gmail.com">dgg.operations@gmail.com</a>. You also have the right to lodge
          a complaint with your local data protection authority.
        </p>
      </LegalSection>

      <LegalSection id="changes" index={16} title="Changes to This Policy">
        <p>
          We may update this Privacy Policy when our practices change or as required by law. When we make material
          changes, we will update the “Last updated” date at the top of this page and, where appropriate, provide notice
          within the app.
        </p>
      </LegalSection>

      <LegalSection id="contact" index={17} title="Contact">
        <p>For any questions or requests regarding this Privacy Policy or your data, contact:</p>
        <div className="rounded-2xl border border-decide-line bg-decide-surface p-5 text-[15px] leading-relaxed">
          <p className="font-semibold text-decide-ink">DECIDE</p>
          <p className="mt-1">Operator: DGG Labs</p>
          <p className="mt-1">Email: <a className="text-decide-forest underline underline-offset-2" href="mailto:dgg.operations@gmail.com">dgg.operations@gmail.com</a></p>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
